import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ResourceSentence } from './resource-sentence';
export { ResourceSentence };
import { resourceSentenceSchema } from '@schemas/business-objects/resource-sentence';
import { ResourceFormGroup } from './resource-form-group';
import type { ExportImportConfig } from '../interface-definitions/index';
import type { TextBox } from './text-box';
import type { User } from './user';

export class ResourceSentenceFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    sentence: SchemaFormControl;
    word: SchemaFormControl;
    sequence: SchemaFormControl;
    locked: SchemaFormControl;
    resourceId: SchemaFormControl;
    resource: ResourceFormGroup;
  };

  declare value: Partial<ResourceSentence>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(resourceSentenceSchema, ResourceSentence, data, options);
  }

  get object(): ResourceSentence {
    return new ResourceSentence(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'resource':
        return new ResourceFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ResourceSentence[]> {
    return ResourceSentence.find(filter);
  }

  static async findOne(filter?: any): Promise<ResourceSentence | null> {
    return ResourceSentence.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<ResourceSentence> {
    return ResourceSentence.findById(id, filter);
  }

  static async create(
    data: Partial<ResourceSentence>,
  ): Promise<ResourceSentence> {
    return ResourceSentence.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<ResourceSentence>,
  ): Promise<ResourceSentence> {
    return ResourceSentence.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return ResourceSentence.deleteById(id);
  }

  async export(): Promise<any> {
    const instance = new ResourceSentence(this.getRawValue());
    return instance.export();
  }

  static async exportMany(body: any): Promise<any> {
    return ResourceSentence.exportMany(body);
  }

  async dataForCopy(): Promise<any> {
    const instance = new ResourceSentence(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class ResourceSentenceFormArray extends PersistedFormArray {
  readonly entityName = 'ResourceSentence' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(resourceSentenceSchema, ResourceSentence, options);
  }

  protected override createItemGroup() {
    return new ResourceSentenceFormGroup();
  }

  get array(): ResourceSentence[] {
    return this.controls.map(
      (group: ResourceSentenceFormGroup) => group.object,
    );
  }
}

export class ResourceSentencePersistedArray extends PersistedArray<ResourceSentence> {
  readonly entityName = 'ResourceSentence' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ResourceSentence, options);
  }
}
